/* eslint-disable jsx-a11y/media-has-caption */
import { forwardRef, useMemo, useReducer, useState } from 'react';

import { insertTrackEvent } from '@comeon/analytics';
import { LoadingSpinner } from '@comeon/loadable';
import Icon, {
	ICON_BELL_LINE,
	ICON_CIRCLE_CHECKMARK_FILLED,
	ICON_CLOSE,
	ICON_VOLUME_OFF,
	ICON_VOLUME_ON
} from '~/components/icon';
import { isSmallerSelector } from '~/components/layout/mediaquery';
import { useTheme } from '~/components/theme/theme-context';
import { TranslationHash } from '~/components/translation/use-translations';
import LazyLoadImage from '~/components/utilities/lazyload-image';
import useCasinoLocation from '~/hooks/use-casino-location';
import { TimingObject } from '~/utils/count-down';

import useLoginFlow from '../../../hooks/use-login-flow';
import { selectPlayerId } from '../../account/ducks';
import { useCasinoSharedDispatch, useCasinoSharedSelector } from '../ducks/types';
import { subscribeGameWidget } from './notification/ducks';
import { getForegroundImageSrcSet, WidgetData } from './utils';
import {
	CenteredContainer,
	CloseButton,
	ComingSoonTitle,
	CountDownWrapper,
	ExclusiveCaption,
	ForegroundImageContainer,
	LogoWrapper,
	MuteButton,
	OtherExclusivesLink,
	SubscribeButton,
	SubscribedText,
	Time,
	TimeAbbreviation,
	TimeWrapper,
	TrailerWrapper,
	VerticalCardTopWrapper,
	VerticalCardWrapper
} from './widget-styles';
import useConfiguration from '~/configuration/use-configuration';

type Props = {
	toggleShowModal: () => void;
	gameName: string;
	timingObject: TimingObject;
	translations: TranslationHash;
} & WidgetData;

const VerticalCardWithRef = forwardRef<HTMLDivElement, Props>(function VerticalCard(
	{
		toggleShowModal,
		gameName,
		foregroundImage,
		backgroundImage,
		otherExclusivesUrl,
		trailerUrl,
		timingObject,
		translations,
		comingSoonText,
		otherExclusivesText,
		previewImage,
		gameLaunch,
		exploreGameItems,
		labelText,
		widgetId
	},
	ref
) {
	const foregroundImageSrcSet = useMemo(() => getForegroundImageSrcSet(foregroundImage, 200), [foregroundImage]);
	const smaller = useCasinoSharedSelector(isSmallerSelector);
	const dispatch = useCasinoSharedDispatch();
	const playerId = useCasinoSharedSelector(selectPlayerId);
	const { subscriptions } = useCasinoSharedSelector(state => state.exclusiveGameWidgets);
	const configuration = useConfiguration();

	const { initializeLogin } = useLoginFlow();

	const [isAnimating, setIsAnimating] = useState(false);
	const { isLiveCasinoLocation } = useCasinoLocation();

	const [videoMuted, toggleVideoMuted] = useReducer(state => {
		return !state;
	}, true);

	const [videoLoaded, toggleVideoLoaded] = useReducer(state => {
		return !state;
	}, false);

	const { LogoSingleColor } = useTheme() as { LogoSingleColor: () => JSX.Element };

	const closeModal = () => {
		insertTrackEvent({
			category: isLiveCasinoLocation ? 'LiveCasino' : 'Casino',
			action: 'WidgetExclusive_Close_X',
			label: '',
			value: ''
		});
		toggleShowModal();
	};

	const trackCTAClick = () => {
		insertTrackEvent({
			category: isLiveCasinoLocation ? 'LiveCasino' : 'Casino',
			action: 'WidgetExclusive_Click_CTA',
			label: '',
			value: ''
		});
	};

	const handleSubscribe = () => {
		insertTrackEvent({
			category: isLiveCasinoLocation ? 'LiveCasino' : 'Casino',
			action: 'WidgetExclusive_Click_RemindMe',
			label: playerId ? 'logged-in' : 'not-logged-in',
			value: ''
		});
		if (!playerId) {
			initializeLogin();
			toggleShowModal();
			return;
		}

		setIsAnimating(true);
		setTimeout(() => setIsAnimating(false), 700);

		dispatch(
			subscribeGameWidget(
				{
					playerId,
					widgetId,
					itemList: exploreGameItems,
					gameLaunch,
					fgImageUrlResourceKey: foregroundImage
				},
				isLiveCasinoLocation
			)
		);
	};

	const handleOtherExclusivesClick = () => {
		trackCTAClick();
		window.location.href = otherExclusivesUrl;
	};

	return (
		<VerticalCardWrapper
			backgroundImage={backgroundImage}
			ref={ref}
			tmpGameIconSrc={configuration?.casino?.tmpGameIcon?.src}
		>
			<VerticalCardTopWrapper>
				<LogoWrapper>
					<LogoSingleColor />
					<ExclusiveCaption>{labelText}</ExclusiveCaption>
				</LogoWrapper>

				<CloseButton onClick={closeModal}>
					<Icon type={ICON_CLOSE} />
				</CloseButton>
			</VerticalCardTopWrapper>
			<ForegroundImageContainer>
				<LazyLoadImage
					src={foregroundImage}
					srcSet={foregroundImageSrcSet}
					sizes="200px"
					alt={`${gameName} image`}
					style={{ width: 'auto' }}
					lazyload
				/>
			</ForegroundImageContainer>
			<TrailerWrapper>
				<video
					style={{ borderRadius: '12px' }}
					height={smaller ? 180 : 270}
					controls={false}
					muted={videoMuted}
					autoPlay
					loop
					playsInline
					onPlaying={toggleVideoLoaded}
					onWaiting={toggleVideoLoaded}
					poster={`${previewImage}?format=auto&height=${smaller ? 180 : 270}&quality=20`}
				>
					<source src={trailerUrl} type="video/mp4" />
				</video>
				{!videoLoaded && <LoadingSpinner pastDelay />}
				<MuteButton onClick={toggleVideoMuted}>
					{videoMuted ? (
						<Icon type={ICON_VOLUME_OFF} width={28} height={28} />
					) : (
						<Icon type={ICON_VOLUME_ON} width={28} height={28} />
					)}
				</MuteButton>
			</TrailerWrapper>
			<ComingSoonTitle>{comingSoonText}</ComingSoonTitle>
			<CountDownWrapper>
				{!!timingObject.days && (
					<TimeWrapper>
						<Time>{String(timingObject.days).padStart(2, '0')}</Time>
						<TimeAbbreviation>{translations.WIDGET_DAYS_ABBREVIATION}</TimeAbbreviation>
					</TimeWrapper>
				)}
				<TimeWrapper>
					<Time>{String(timingObject.hours).padStart(2, '0')}</Time>
					<TimeAbbreviation>{translations.WIDGET_HOURS_ABBREVIATION}</TimeAbbreviation>
				</TimeWrapper>
				<TimeWrapper>
					<Time>{String(timingObject.minutes).padStart(2, '0')}</Time>
					<TimeAbbreviation>{translations.WIDGET_MINUTES_ABBREVIATION}</TimeAbbreviation>
				</TimeWrapper>
				{!timingObject.days && (
					<TimeWrapper>
						<Time>{String(timingObject.seconds).padStart(2, '0')}</Time>
						<TimeAbbreviation>{translations.WIDGET_SECONDS_ABBREVIATION}</TimeAbbreviation>
					</TimeWrapper>
				)}
			</CountDownWrapper>
			<CenteredContainer>
				{subscriptions?.find(subscription => subscription.widgetId === widgetId) ? (
					<SubscribedText>
						<Icon type={ICON_CIRCLE_CHECKMARK_FILLED} />
						{translations.REMINDER_SET}
					</SubscribedText>
				) : (
					<SubscribeButton onClick={handleSubscribe} isAnimating={isAnimating}>
						<Icon type={ICON_BELL_LINE} /> {translations.REMIND_ME}
					</SubscribeButton>
				)}
				<OtherExclusivesLink onClick={handleOtherExclusivesClick} href={otherExclusivesUrl}>
					{otherExclusivesText}
				</OtherExclusivesLink>
			</CenteredContainer>
		</VerticalCardWrapper>
	);
});

export default VerticalCardWithRef;
